import React, { useState, useEffect, useMemo, useRef, useCallback,  Dispatch, MouseEvent } from 'react';
import _,{ DebouncedFunc } from 'lodash';
// import Select, { ActionMeta } from 'react-select';
import { WithContext as ReactTags } from 'react-tag-input';
import Dropzone from './Dropzone';
import { Tooltip } from 'react-tooltip';
import Modal from 'react-modal';

import { API_PATH } from '../constants';
import { Role } from '../Classes1';
import { IScenario, ITag, IRole, ITempRole, IGameStatusSummary, IInject } from '../interfaces';

import '../App.css';
import 'react-tooltip/dist/react-tooltip.css'
import { http } from '../utils/config';
import { useAppDispatch } from '../app/hooks';
import { deleteGame } from '../features/player/gameSlice';
import { Copy as CopyIcon, Delete as DeleteIcon } from '@carbon/icons-react';

const defaultModalStyles = {
    content: {
      background: '#323544',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      padding: '0',
      border: 'none',
      width: '450px',
      transform: 'translate(-50%, -50%)',
    }
};

interface ScenarioSettingsProps {
    updateShowHide: Dispatch<any>;
    setSelectedScenario: Dispatch<any>;
    selectedScenario: IScenario;
    showSettings: string;
    errorArray: string[];
    onChangeSave: DebouncedFunc<any>;
    onError: Dispatch<string>;
    copyScenarioLoading: boolean;
    openCopyScenarioModal: () => void;
    saveScenario: (i: IInject[]) => Promise<void>;
  }

    const KeyCodes = {
        comma: 188,
        enter: 13
    };

    const delimiters = [KeyCodes.comma, KeyCodes.enter];

    const SCENARIO_NAME_MAX = 100;
    const SCENARIO_DESCRIPTION_MAX = 500;
    const SCENARIO_BRIEFING_MAX = 5000;

    const MAX_IMAGES = 10;
    const MAX_IMAGE_SIZE = process.env.REACT_APP_IMAGE_SRC === 'cloud' ? 4000000 : 500000;
    const MAX_IMAGE_SIZE_LABEL = MAX_IMAGE_SIZE / 1000;
  
const ScenarioSettings: React.FC<ScenarioSettingsProps> = ({ setSelectedScenario, selectedScenario, showSettings, errorArray, onChangeSave, onError, copyScenarioLoading, openCopyScenarioModal, saveScenario }) => {
    const dispatch = useAppDispatch();

    const [majoritySelected, setMajoritySelected] = useState<string>('')
    const [unanimousSelected, setUnanimousSelected] = useState<string>('')
    const [newUser, setNewUser] = useState<string>('')
    const [newParticipant, setNewParticipant] = useState<string>('')
    const [newAvailRoles, setNewAvailRoles] = useState<string>('')

    const [showSettingsMenu, setShowSettingsMenu] = useState<string>('selected')
    const [showBriefingMenu, setShowBriefingMenu] = useState<string>('')
    const [showSettingsPanel, setShowSettingsPanel] = useState<string>('show')
    const [showBriefingPanel, setShowBriefingPanel] = useState<string>('hide')
    // const [selectedOption, setSelectedOption] = useState<ISelectedOption | null>(new SelectedOption({}))
    const [totalRoles, setTotalRoles] = useState<IRole[]>([])
    const [roleCountHigh, setRoleCountHigh] = useState<string>('hide')
    const [totalRoleCount, setTotalRoleCount] = useState<number>(0)
    const [disablePublish, setDisablePublish] = useState<boolean>(false)
    const [disableFeatured, setDisableFeatured] = useState<boolean>(false)
    const [liveGameCount, setLiveGameCount] = useState<number>(0);
    const [deletableGame, setDeletableGame] = useState<IGameStatusSummary | undefined>();
    const [deleteGameModalIsOpen, setDeleteGameModalIsOpen] = useState<boolean>(false);

    const maxRoleCount: number = 6;

    const queryParameters = new URLSearchParams(window.location.search)
    const scenarioId: string | null = queryParameters.get("scenarioid")

    const closeDeleteGameModal = () => {
        setDeleteGameModalIsOpen(false);
    }

    const scenarioRef = useRef(selectedScenario);
    scenarioRef.current = selectedScenario
    const liveGamePoll = useMemo(() => async () => {
        if (!_.isEmpty(scenarioId)) {
            try {
                const resp = await http.get(`${API_PATH}/scenario/${scenarioId}/games`);
                const games = resp.data as IGameStatusSummary[];
                setLiveGameCount(games.filter((g) => !g.completed).length)
                setDeletableGame(games.find((g) => g.isHost));
            } catch (e) {
                console.log('Error fetching active game list:', e);
            }
        }
        return 0;
    }, [scenarioId]);

    const scenarioCheckRef = useRef<any>(0);
    useEffect(() => {
        liveGamePoll() // Run it the first time.
    },[]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (scenarioCheckRef) {
            clearInterval(scenarioCheckRef.current);
        }
        scenarioCheckRef.current = setInterval(liveGamePoll, 4000);
    }, [liveGamePoll, selectedScenario.id]);

    useEffect(() => {
        if (showSettings === 'show') {
            if (_.isEmpty(scenarioId)) {
                onChangeSave.flush();
            }
        }
    }, [showSettings]) // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        let participantsPlusRoles: string[] = [];
        let totRoles: IRole[] = [];

        participantsPlusRoles = JSON.parse(JSON.stringify(selectedScenario.roles));
        participantsPlusRoles.forEach((role: string, i: number) => {
            let totRole: IRole = new Role()
            totRole.text = role
            totRole.count = '0';
            totRole.order = i;
            totRoles.push(totRole)
        })

        const counts: Record<string, any> = {};
        participantsPlusRoles.forEach(function (x: any) { counts[x] = (counts[x] || 0) + 1; });
        for (let prop in counts) {
            totRoles.forEach((role: IRole) => {
                if (prop === role.text) {
                    role.count = (counts as any)[prop].toString()
                }
            })   
        }

        const uniqueArray = totRoles.filter((obj, index) => {
            return index === totRoles.findIndex(o => obj.text === o.text);
        });

        setTotalRoleCount(uniqueArray.reduce(getSum, 0))
        setTotalRoles(uniqueArray)

        setSelectedScenario({
            ...selectedScenario, 
            totalRoles: uniqueArray
        });

    }, [selectedScenario.roles])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        switch(selectedScenario.votingStyle) {
            case 'majority':
                setMajoritySelected('selected')
                setUnanimousSelected('')
            break;
            case 'unanimous':
                setMajoritySelected('')
                setUnanimousSelected('selected')
            break;
            default:
            break;
        }
    }, [selectedScenario.votingStyle])

    const selectVotingStyle = (ev: MouseEvent) => {
        if (ev.currentTarget instanceof HTMLElement) {
            setSelectedScenario({
                ...selectedScenario, 
                votingStyle: ev.currentTarget.dataset.id 
            });
            switch(ev.currentTarget.dataset.id) {
                case 'majority':
                    setMajoritySelected('selected')
                    setUnanimousSelected('')
                  break;
                case 'unanimous':
                    setMajoritySelected('')
                    setUnanimousSelected('selected')
                  break;
                default:
                  break;
              }
        }
    }

    const onChangePublished = async () => {
        setDisablePublish(true)
        setTimeout(() => {
            setDisablePublish(false)
        }, 3000)
        setSelectedScenario({
            ...selectedScenario, 
            published: !selectedScenario.published
        });
    }

    const onChangeFeatured = async () => {
        setDisableFeatured(true)
        setTimeout(() => {
            setDisableFeatured(false)
        }, 3000)
        setSelectedScenario({
            ...selectedScenario, 
            featured: !selectedScenario.featured
        });
    }

    const changeRoleText = (ev: React.ChangeEvent<HTMLInputElement>) => {
        onChangeSave()

        const target = ev.currentTarget as HTMLInputElement;

        if (target.value.length > 4) {
            return;
        }
        
        if (target) {
            let tempRoles: any = JSON.parse(JSON.stringify(totalRoles));
            tempRoles.forEach((role: IRole) => { 
                if (role.text === target.dataset.role && role.order === parseInt(target.dataset.order!)) {
                    role.text = target.value
                }
            });
            setTotalRoles(tempRoles)

            let newRoles: string[] = []

            if (tempRoles.length > 0) {
                tempRoles.forEach((role: IRole) => {
                for (let j = 0; j<parseInt(role.count); j++) {
                    newRoles.push(role.text)
                }
                })
            }

            setSelectedScenario({
                ...selectedScenario, 
                totalRoles: tempRoles,
                roles: newRoles
            });
        }

    }

    const changeRoleNum = (ev: React.ChangeEvent<HTMLInputElement>) => {

        const target = ev.currentTarget as HTMLInputElement;
        
        if (target) {
            if (isNaN(parseInt(target.value)) && target.value !== '') {
                return;
            } else if (target.value === '' || target.value === '0') {
                let tempRoles: any = JSON.parse(JSON.stringify(totalRoles));
                tempRoles.forEach((role: IRole) => { 
                    if (role.text === target.dataset.role && role.order === parseInt(target.dataset.order!)) {
                         role.count = ''
                    }
                 });
                 setTotalRoles(tempRoles)
                 setSelectedScenario({
                    ...selectedScenario, 
                    totalRoles: tempRoles
                });

            } else if (isNaN(parseInt(target.value))) {
                return;
            } else {
                let totRoles = JSON.parse(JSON.stringify(totalRoles))
                let tempRoles: any = JSON.parse(JSON.stringify(totalRoles.filter((role) => role.text)))
                
                tempRoles.forEach((role: IRole) => {
                    if (role.text === target.dataset.role) {
                        role.count = target.value
                    }
                });

                totRoles.forEach((role1: ITempRole) => {
                    tempRoles.forEach((role2: IRole) => {
                        if (role1.text === role2.text) {
                            role1.count = parseInt(role2.count)
                        }
                    });
                })   
               
                const uniqueArray = totRoles.filter((obj: any, index: number) => {
                    return index === totRoles.findIndex((o: any) => obj.text === o.text);
                });

                if (uniqueArray.reduce(getSum, 0) > maxRoleCount) {
                    setRoleCountHigh('show-inline');
                    onError(`${maxRoleCount} is the max number of roles allowed on this scenario.`);
                } else {
                    setTotalRoleCount(uniqueArray.reduce(getSum, 0))
                    setRoleCountHigh('hide')
                    setTotalRoles(uniqueArray)
                    
                    let newRoles: string[] = []
                    if (uniqueArray.length > 0) {
                        uniqueArray.forEach((role: IRole) => {
                          for (let j = 0; j<parseInt(role.count); j++) {
                            newRoles.push(role.text)
                          }
                        })
                    }                    

                    setSelectedScenario({
                        ...selectedScenario, 
                        totalRoles: tempRoles,
                        roles: newRoles
                    });
                }
            }
        }
    }

    const changeSharedWith = (ev: React.ChangeEvent<HTMLInputElement>) => {

        const target = ev.currentTarget as HTMLInputElement;
        
        if (target) {
           
            let sharedWith: string[] = JSON.parse(JSON.stringify(selectedScenario.sharedWith))

            sharedWith.forEach((item, i, arr) => {
                if (target.dataset.i === i.toString()) {
                    arr[i] = target.value
                }
            })

            setSelectedScenario({
                ...selectedScenario, 
                sharedWith: sharedWith
            });
        }
    }

    const changeNewParticipant = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const target = ev.currentTarget as HTMLInputElement;

        if (target.value.length > 4) {
            return;
        }
        
        if (target) {
            setNewParticipant(target.value);
        }
    }

    const changeAvailRoles = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const target = ev.currentTarget as HTMLInputElement;
        
        if (target) {
            if (!isNaN(parseInt(target.value)) && target.value !== '') {
                setNewAvailRoles(target.value);
            }
        }
    }

    const addNewParticipant = (ev: MouseEvent) => {
        if (_.isEmpty(newParticipant) || _.isEmpty(newAvailRoles)) {
            return;
        }
        let tempRoles: any = JSON.parse(JSON.stringify(totalRoles));

        let newRole: IRole = new Role();
        newRole.text = newParticipant;
        newRole.count = newAvailRoles;
        newRole.order = totalRoles.length;
        tempRoles.push(newRole);

        if (tempRoles.reduce(getSum, 0) > maxRoleCount) {
            setRoleCountHigh('show-inline')
            onError(`${maxRoleCount} is the max number of roles allowed on this scenario.`);
        } else {

            let totRoleCount: number = 0
            tempRoles.forEach((role: IRole) => {
                totRoleCount += +role.count
            })
            setTotalRoleCount(totRoleCount)
            setTotalRoles(tempRoles)
            setNewParticipant('')
            
            let newRoles: string[] = []
            if (tempRoles.length > 0) {
                tempRoles.forEach((role: IRole) => {
                  for (let j = 0; j<parseInt(role.count); j++) {
                    newRoles.push(role.text)
                  }
                })
            }

            setSelectedScenario({
                ...selectedScenario, 
                totalRoles: tempRoles,
                roles: newRoles
            });
        }

        setNewAvailRoles('')
    }

    const deleteParticipant = (ev: MouseEvent) => {
        const target = ev.currentTarget as HTMLInputElement;
        
        if (target) {
            let tempRoles: IRole[] = JSON.parse(JSON.stringify(totalRoles));
            
            tempRoles.forEach((role: IRole, i: number) => { 
                if (role.text === target.dataset.role && role.order === parseInt(target.dataset.order!)) {
                    tempRoles.splice(i, 1);
                }
            });

            let totalRolesCount: number = 0

            tempRoles.forEach((role: IRole, i: number) => { 
                totalRolesCount += +role.count
            })

            setTotalRoleCount(totalRolesCount)
            setTotalRoles(tempRoles)
            
            let newRoles: string[] = []
            if (tempRoles.length > 0) {
                tempRoles.forEach((role: IRole) => {
                for (let j = 0; j<parseInt(role.count); j++) {
                    newRoles.push(role.text)
                }
                })
            }

            setSelectedScenario({
                ...selectedScenario, 
                totalRoles: tempRoles,
                roles: newRoles
            });
        }
    }

    const changeNewSharedWith = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const target = ev.currentTarget as HTMLInputElement;
        
        if (target) {
            setNewUser(target.value);
        }
    }

    const addSharedWith = (ev: MouseEvent) => {
        let sharedWith: string[] = JSON.parse(JSON.stringify(selectedScenario.sharedWith))

        if (_.isEmpty(newUser)) {
            return;
        }
        
        sharedWith.push(newUser)
        setNewUser('')

        setSelectedScenario({
            ...selectedScenario, 
            sharedWith: sharedWith
        });
    }

    const deleteSharedWith = (ev: MouseEvent) => {
        const target = ev.currentTarget as HTMLInputElement;
        if (target) {
            setSelectedScenario({
                ...selectedScenario, 
                sharedWith: selectedScenario.sharedWith.filter((data: any, k: number) => k.toString() !== target.dataset.i )
            });
        }
    }

    const onChangeName = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const target = ev.currentTarget as HTMLInputElement;
        if (!target) { return }

        let newValue = target.value.slice();
        if (newValue.length <= SCENARIO_NAME_MAX) {
            newValue = newValue.slice(0, SCENARIO_NAME_MAX);
        }
        setSelectedScenario({
            ...selectedScenario, 
            name: newValue,
        });
        onChangeSave();
    }

    const [workingScenarioId, setWorkingScenarioId] = useState(selectedScenario.id);
    const [workingDesc, setWorkingDesc] = useState(selectedScenario.description);
    const [workingBriefing, setWorkingBriefing] = useState(selectedScenario.briefing);
    // Reset working data if we change scenarios.
    useEffect(() => {
        if (workingScenarioId !== selectedScenario.id) {
            setWorkingBriefing(selectedScenario.briefing);
            setWorkingDesc(selectedScenario.description);
            setWorkingScenarioId(selectedScenario.id)
            return;
        }
    },[
        workingScenarioId, selectedScenario.id,
        workingDesc, selectedScenario.description,
        workingBriefing, selectedScenario.briefing
    ]);

    const [ descHeight, setDescHeight ] = useState<string>('75px');
    const onDescriptionFocus = useCallback((evt: React.FocusEvent<HTMLTextAreaElement>) => {
        const box = evt.currentTarget;
        const height = box.computedStyleMap().get('height')?.toString() || '';
        setDescHeight(height);
        box.style.height = `${box.scrollHeight}px`;
    }, [])

    const onDescriptionBlur = useCallback((evt: React.FocusEvent<HTMLTextAreaElement>) => {
        const box = evt.currentTarget;
        box.style.height = descHeight;
    }, [descHeight])

    const [ briefingHeight, setBriefingHeight ] = useState<string>('400px');
    const onBriefingFocus = useCallback((evt: React.FocusEvent<HTMLTextAreaElement>) => {
        const box = evt.currentTarget;
        const height = box.computedStyleMap().get('height')?.toString() || '';
        setBriefingHeight(height);
        box.style.height = `${box.scrollHeight}px`;
    }, [])

    const onBriefingBlur = useCallback((evt: React.FocusEvent<HTMLTextAreaElement>) => {
        const box = evt.currentTarget;
        box.style.height = briefingHeight;
    }, [briefingHeight])


    const onChangeDescription = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const target = ev.currentTarget;
        if (!target) { return; }

        let workValue = target.value.slice();
        setWorkingDesc(workValue);
        target.style.height = `auto`;
        target.style.height = `${target.scrollHeight}px`;
        // Truncate server value before save.
        if (workValue.length > SCENARIO_DESCRIPTION_MAX) {
            workValue = workValue.slice(0, SCENARIO_DESCRIPTION_MAX);
        }

        // Save only if changed.
        if (workValue !== selectedScenario.description) {
            setSelectedScenario({
                ...selectedScenario, 
                description: workValue,
            });
            onChangeSave();
        }
    }

    const onChangeBriefing = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const target = ev.currentTarget;
        if (!target) { return; }
        let workValue = target.value.slice();
        setWorkingBriefing(workValue);

        // Truncate server value before save.
        if (workValue.length > SCENARIO_BRIEFING_MAX) {
            workValue = workValue.slice(0, SCENARIO_BRIEFING_MAX);
        }

        if (workValue !== selectedScenario.briefing) {
            setSelectedScenario({
                ...selectedScenario, 
                briefing: workValue
            });
            onChangeSave();
        }
    }

    const clickSelectPanel = (ev: MouseEvent) => {
        if (ev.currentTarget instanceof HTMLElement) {
            switch(ev.currentTarget.dataset.id) {
                case 'settings':
                    setShowSettingsMenu('selected')
                    setShowSettingsPanel('show')
                    setShowBriefingMenu('')
                    setShowBriefingPanel('hide')
                  break;
                case 'briefing':
                    setShowSettingsMenu('')
                    setShowSettingsPanel('hide')
                    setShowBriefingMenu('selected')
                    setShowBriefingPanel('show')
                  break;
                case 'variables':
                    setShowSettingsMenu('')
                    setShowSettingsPanel('hide')
                    setShowBriefingMenu('')
                    setShowBriefingPanel('hide')
                  break;
                default:
                  break;
              }    
        }
    }
    
      const handleLocationDelete = (i: number) => {
        setSelectedScenario({
            ...selectedScenario,
            tagsLocation: selectedScenario.tagsLocation.filter((tag, index) => index !== i) 
        });
      };
    
      const handleLocationAddition = (tag: ITag) => {
        setSelectedScenario({
            ...selectedScenario,
            tagsLocation: [...selectedScenario.tagsLocation, tag.text!.toLowerCase()]
        });
      };
    
      const handleLocationDrag = (tag: ITag, currPos: number, newPos: number) => {
        const newTags = selectedScenario.tagsLocation.slice();
    
        newTags.splice(currPos, 1);
        newTags.splice(newPos, 0, tag.text!);
    
        // re-render
        setSelectedScenario({
            ...selectedScenario, 
            tagsLocation: newTags
        });
      };

      const handleDetailDelete = (i: number) => {
        setSelectedScenario({
            ...selectedScenario, 
            tagsDetails: selectedScenario.tagsDetails.filter((tag, index) => index !== i) 
        });
      };
    
      const handleDetailAddition = (tag: ITag) => {
        setSelectedScenario({
            ...selectedScenario, 
            tagsDetails: [...selectedScenario.tagsDetails, tag.text!.toLowerCase()]
        });
      };
    
      const handleDetailDrag = (tag: ITag, currPos: number, newPos: number) => {
        const newTags = selectedScenario.tagsDetails.slice();
    
        newTags.splice(currPos, 1);
        newTags.splice(newPos, 0, tag.text!);
    
        // re-render
        setSelectedScenario({
            ...selectedScenario,
            tagsDetails: newTags
        });
      };

      const handleTopicDelete = (i: number) => {
        setSelectedScenario({
            ...selectedScenario, 
            tagsTopic: selectedScenario.tagsTopic.filter((tag, index) => index !== i) 
        });
      };
    
      const handleTopicAddition = (tag: ITag) => {
        setSelectedScenario({
            ...selectedScenario, 
            tagsTopic: [...selectedScenario.tagsTopic, tag.text!.toLowerCase()]
        });
      };
    
      const handleTopicDrag = (tag: ITag, currPos: number, newPos: number) => {
        const newTags = selectedScenario.tagsTopic.slice();
    
        newTags.splice(currPos, 1);
        newTags.splice(newPos, 0, tag.text!);
    
        // re-render
        setSelectedScenario({
            ...selectedScenario,
            tagsTopic: newTags
        });
      };

      const [deleteGameLoading, setDeleteGameLoading] = useState<boolean>(false);
      const onDeleteGame = async () => {
        if (deleteGameLoading || !deletableGame) {
            setDeleteGameModalIsOpen(false);
            return;
        }
        setDeleteGameLoading(true);
        await dispatch(deleteGame(deletableGame.gameId));
        liveGamePoll();
        setDeleteGameLoading(false);
        setDeleteGameModalIsOpen(false);
      }

    /* NOTE: Not removing because not sure if we want to revive this functionality in the short term.
      const handleChange = async (selectedOption: ISelectedOption | null, actionMeta: ActionMeta<ISelectedOption>) => {
        setSelectedOption(selectedOption)
      }
      const options: ISelectedOption[] = [];

      const dropdownStyles = {
        option: (provided: any, { isFocused, isSelected } : { isFocused: any, isSelected: any }) => ({
          ...provided,
          backgroundColor: isFocused ? "#393939" : "#191B22",
          fontSize: 14
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            padding: 0,
            color: '#AAADBC',
            backgroundColor: "#191B22",
            width: 160,
            fontSize: 14
        }),
        container:  (provided: any, state: any) => ({
          ...provided,
          backgroundColor: "#191B22",
          border: "none",
          borderBottom: "1px solid #383C4D",
          width: 160,
          height: 40
        }),
        control:  (provided: any, state: any) => ({
          ...provided,
          backgroundColor: "#191B22",
          color: "#AAADBC",
          borderRadius: 0,
          border: "none",
          boxShadow: 0,
          width: 160
        }),
        placeholder:  (provided: any, state: any) => ({
          ...provided,
          color: "#AAADBC",
          fontSize: 14
        }),
        indicatorSeparator:  (provided: any, state: any) => ({
          ...provided,
          display: "none"
        }),
        menu:  (provided: any, state: any) => ({
          ...provided,
          width: 160,
          color: '#AAADBC',
          backgroundColor: "#191B22",
          top: 31
        }),
        menuList:  (provided: any, state: any) => ({
          ...provided,
          color: '#AAADBC',
          backgroundColor: "#191B22",
          paddingTop: 0,
          paddingBottom: 0
        }),
        indicatorContainer:  (provided: any, state: any) => ({
          ...provided,
          color: '#191B22'
        })
      }
 
      options.push({ value: "1", label: "Value 1" })
      options.push({ value: "2", label: "Value 2" })
    */

      const getSum = (total: any, num: any) => {
        if (num.count === '') {
            return total;
        } else {
            return total + parseInt(num.count);   
        }
      }

      //const disableImageUpload = !selectedScenario || !selectedScenario.images || (selectedScenario.images.length >= MAX_IMAGES);
      const uiTagsDetail = useMemo(() => selectedScenario.tagsDetails.map((tag) => ({
        id: tag,
        text: tag,
      })), [selectedScenario.tagsDetails])

      const uiTagsLocation = useMemo(() => selectedScenario.tagsLocation.map((tag) => ({
        id: tag,
        text: tag,
      })), [selectedScenario.tagsLocation])

      const uiTagsTopic = useMemo(() => selectedScenario.tagsTopic.map((tag) => ({
        id: tag,
        text: tag,
      })), [selectedScenario.tagsTopic])

      // TODO. Add some kind of cookie or localstore value signal we never want to show this again.
      const tagTooltipRef = useRef<any>()
      const [ endTooltipCount, setEndTooltipCount ] = useState(4);
      const openTagHelper = (target: string) => {
        if (endTooltipCount > 0) {
            tagTooltipRef.current?.open({anchorSelect: `#${target}`})
            setEndTooltipCount(endTooltipCount - 1);
        }
      }
      const closeTagTooltip = () => {
        tagTooltipRef.current?.close()
      }

    return (
        <div className={`scenario-settings ${showSettings}`}>
            {errorArray.map((item) => {
                return <div className="settings-errors">Error: {item}</div>
            })}
                <div className={`settings-menu`}>
                    <div className={`settings-menu-item ${showSettingsMenu}`} onClick={clickSelectPanel} data-id="settings">
                        Settings
                    </div>
                    <div className={`settings-menu-item ${showBriefingMenu}`} onClick={clickSelectPanel} data-id="briefing">
                        Briefing
                    </div>
                </div>
                <div className={`briefing-panel ${showBriefingPanel}`}>
                    <div className="briefing-title">Briefing</div>
                    <textarea name="briefing-desc" className="briefing-desc"
                        value={workingBriefing} onChange={onChangeBriefing}
                        onFocus={onBriefingFocus} onBlur={onBriefingBlur}
                        disabled={selectedScenario.published ? true : false}></textarea>
                    <div className="scenario-desc-helper">
                        <span style={{color: workingBriefing.length > SCENARIO_BRIEFING_MAX ? 'red' : undefined}}>{workingBriefing.length} / {SCENARIO_BRIEFING_MAX} char</span>
                        {workingBriefing.length > SCENARIO_BRIEFING_MAX && ` Briefing will not autosave until it fewer than ${SCENARIO_BRIEFING_MAX} characters.`} 
                    </div>
                </div>
                {/*
                <div className={`variables-panel`}>
                    <div className="settings-row name-row">
                        <div className="settings-label name-label">
                            <div className="settings-label-title">VARIABLES</div>
                            <div className="settings-label-desc">Configure scenario variables you wish to use in your scenario here.</div>
                        </div>
                        <div className="settings-content">
                        <div className="variables">
                                <div className="variables-labels">
                                    <div className="variables-label-variable">Variable</div>
                                    <div className="variables-label-type">Type</div>
                                    <div className="variables-label-value">Initial Value</div>
                                </div>
                                <div className="variables-row">
                                    <input className="variables-text" value="A-1" onChange={changeVarText} />
                                    <Select
                                        value={selectedOption}
                                        onChange={handleChange}
                                        options={options}
                                        styles={dropdownStyles}
                                        className={'variables-select-styles'}
                                    />
                                    <input className="variables-num" value="0" onChange={changeNumText} />
                                    <button className="variables-trash">
                                        <img src="img/trash-can.svg" alt="trash" />
                                    </button>
                                </div>
                                <div className="variables-row">
                                    <input className="variables-text" value="A-2"  onChange={changeVarText} />
                                    <Select
                                        value={selectedOption}
                                        onChange={handleChange}
                                        options={options}
                                        styles={dropdownStyles}
                                        className={'variables-select-styles'}
                                    />
                                    <input className="variables-num" value="1" onChange={changeNumText} />
                                    <button className="variables-trash">
                                        <img src="img/trash-can.svg" alt="trash" />
                                    </button>
                                </div>
                                <div className="variables-row">
                                    <input className="variables-text" value="A-3"  onChange={changeVarText} />
                                    <Select
                                        value={selectedOption}
                                        onChange={handleChange}
                                        options={options}
                                        styles={dropdownStyles}
                                        className={'variables-select-styles'}
                                    />
                                    <input className="variables-num" value="1" onChange={changeNumText} />
                                    <button className="variables-trash">
                                        <img src="img/trash-can.svg" alt="trash" />
                                    </button>
                                </div>
                                <button className="add-variable-btn">Add Variable +</button>
                            </div>
                        </div>
                    </div>
                </div>
                */}
                <div className={`settings-panel ${showSettingsPanel}`}>
                <div className="settings-labels">
                    <div className="settings-row name-row">
                        <div className="settings-label name-label">
                            <div className="settings-label-title">Name</div>
                            <div className="settings-label-desc">The name of the scenario. Appears in the Scenario Library so make it memorable!</div>
                        </div>
                        <div className="settings-content">
                            {selectedScenario.published
                            ? <input type="text" name="scenario-title" className="scenario-title" value={selectedScenario.name} disabled/>
                            : <input type="text" name="scenario-title" className="scenario-title" value={selectedScenario.name} onChange={onChangeName} maxLength={100} />
                            }
                            <div className="scenario-desc-helper" style={{color: selectedScenario.name.length > SCENARIO_NAME_MAX ? '#DA1E28' : undefined}}>{selectedScenario.name.length} / {SCENARIO_NAME_MAX} char</div>
                        </div>
                    </div>
                    <div className="settings-row name-row">
                        <div className="settings-label name-label">
                            <div className="settings-label-title">Published</div>
                            <div className="settings-label-desc"><span style={{fontWeight: selectedScenario.published ? '600' : undefined, color: selectedScenario.published ? 'white' : undefined}}>The Scenario needs to be in "Unpublished" mode in order to edit,</span> "Published" to be available to players side.</div>
                        </div>
                        <div className="settings-content">
                            <div className="published-checkbox">
                                {!_.isEmpty(selectedScenario.id) ? 
                                    <div className="checkbox-wrapper-7">
                                        <input className="tgl tgl-ios" id="cb2-7" type="checkbox" value={selectedScenario.published!.toString()} checked={selectedScenario.published} onChange={onChangePublished} disabled={(liveGameCount > 0 && selectedScenario.published) || disablePublish} />
                                        <label className={`tgl-btn ${(liveGameCount > 0) || disablePublish ? 'disabled' : ''}`} htmlFor="cb2-7"></label>
                                    </div> : <div>A Scenario must be saved first before being able to publish.</div>
                                }
                            </div>
                            {selectedScenario.published && (liveGameCount > 0) &&
                                <div style={{marginTop: '1em'}} className='settings-publish-info'>
                                    Cannot toggle while live games are running this scenario.
                                </div>
                            }
                            {selectedScenario.published && (liveGameCount === 1 && deletableGame) &&
                                <div className='settings-publish-info'>
                                    As the host of the only game, you can choose to delete the game to unlock this toggle.
                                </div>
                            }
                            {selectedScenario.published && (liveGameCount > 0) &&
                                <div className='settings-publish-info'>
                                    <button className="default" disabled={copyScenarioLoading} onClick={openCopyScenarioModal}><CopyIcon size={14}/> Edit A Copy</button>
                                    {selectedScenario.published && (liveGameCount === 1 && deletableGame) &&
                                    <>
                                    &nbsp; <button className="default" disabled={deleteGameLoading} onClick={() => setDeleteGameModalIsOpen(true)}><DeleteIcon size={14}/> Delete Game</button>
                                    </>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                    <div className="settings-row name-row">
                        <div className="settings-label name-label">
                            <div className="settings-label-title">Featured</div>
                            <div className="settings-label-desc">Flag this Scenario as 'Featured' and it will appear in the Featured tab on the game home.</div>
                        </div>
                        <div className="settings-content">
                            <div className="published-checkbox">
                                <div className="checkbox-wrapper-9">
                                    <input className="tgl tgl-ios" id="cb2-9" type="checkbox" value={selectedScenario.featured.toString()} checked={selectedScenario.featured} onChange={onChangeFeatured} disabled={disableFeatured} /><label className="tgl-btn" htmlFor="cb2-9"></label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="settings-row description-row">
                        <div className="settings-label desc-label">
                            <div className="settings-label-title">Description</div>
                            <div className="settings-label-desc">Give your scenario a brief description.</div>
                        </div>
                        <div className="settings-content">
                            <div className="scenario-desc-content">
                                {selectedScenario.published
                                ? <textarea name="scenario-desc" className="scenario-desc" value={workingDesc} disabled />
                                : <textarea name="scenario-desc" className="scenario-desc"
                                    value={workingDesc} onChange={onChangeDescription}
                                    onFocus={onDescriptionFocus} onBlur={onDescriptionBlur}
                                    />
                                }
                                <div className="scenario-desc-helper">
                                    <span style={{color: workingDesc.length > SCENARIO_DESCRIPTION_MAX ? 'red' : undefined}}>{workingDesc.length} / {SCENARIO_DESCRIPTION_MAX} char</span>
                                    {workingDesc.length > SCENARIO_DESCRIPTION_MAX && ` Description will not autosave until it fewer than ${SCENARIO_DESCRIPTION_MAX} characters.`
                                }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="settings-row tags-row">
                        <div className="settings-label tags-label">
                            <div className="settings-label-title">Tags</div>
                            <div className="settings-label-desc">Add existing or create and add new tags to help identify your scenario.</div>
                        </div>
                        <div className="settings-content">
                            <div className="scenario-turns-content clearfix">
                                {selectedScenario.published ? <div className="scenario-disabled"></div> : <></> }
                                <div className="scenario-tags-title">Details</div>
                                <ReactTags
                                    id="tags-details"
                                    tags={uiTagsDetail}
                                    delimiters={delimiters}
                                    handleDelete={handleDetailDelete}
                                    handleAddition={handleDetailAddition}
                                    handleDrag={handleDetailDrag}
                                    placeholder={"Click Here to Add Search Tag"}
                                    inputFieldPosition="bottom"
                                    autocomplete
                                    handleInputFocus={() => { openTagHelper('tags-details') }}
                                    handleInputBlur={closeTagTooltip}
                                />
                                <div className="scenario-tags-title">Locations</div>
                                <ReactTags
                                    id="tags-locations"
                                    tags={uiTagsLocation}
                                    delimiters={delimiters}
                                    handleDelete={handleLocationDelete}
                                    handleAddition={handleLocationAddition}
                                    handleDrag={handleLocationDrag}
                                    placeholder={"Click Here to Add Search Tag"}
                                    inputFieldPosition="bottom"
                                    autocomplete
                                    handleInputFocus={() => { openTagHelper('tags-locations') }}
                                    handleInputBlur={closeTagTooltip}
                                />
                                <div className="scenario-tags-title">Topics</div>
                                <ReactTags
                                    id="tags-topics"
                                    tags={uiTagsTopic}
                                    delimiters={delimiters}
                                    handleDelete={handleTopicDelete}
                                    handleAddition={handleTopicAddition}
                                    handleDrag={handleTopicDrag}
                                    placeholder={"Click Here to Add Search Tag"}
                                    inputFieldPosition="bottom"
                                    autocomplete
                                    handleInputFocus={() => { openTagHelper('tags-topics') }}
                                    handleInputBlur={closeTagTooltip}
                                />
                            </div>
                        </div>
                    </div>
                    <Tooltip id="tags-tooltip" ref={tagTooltipRef} className="sidemenu-tooltip" content="Press Enter after typing to insert tag" place="bottom-start"/>

                    <div className="settings-row images-row">
                        <div className="settings-label feature-images-label">
                            <div className="settings-label-title">Feature Images</div>
                            <div className="settings-label-desc">Images used to promote the scenario in the Scenario Library.</div>
                        </div>
                        <div className="settings-content">
                                <div className="feature-images-content">
                                { selectedScenario.published ? <div className="scenario-disabled"></div> : <></> }
                                    <div className="feature-image">
                                        <div className="feature-image-label">Upload Files (Max {MAX_IMAGES})</div>
                                        <div className="feature-image-text">Max file size is {MAX_IMAGE_SIZE_LABEL}kb. Supported file types are .jpg and .png.</div>

                                        <Dropzone onError={onError} setSelectedScenario={setSelectedScenario} selectedScenario={selectedScenario} onChangeSave={onChangeSave} disabled={selectedScenario.images.length >= 5} type="scenario" saveScenario={saveScenario} />

                                    </div>
                                </div>
                        </div>
                    </div>
                    <div className="settings-row roles-row">
                        <div className="settings-label roles-label">
                            <div className="settings-label-title">Roles</div>
                            <div className="settings-label-desc">Configure the  available roles  and quantity of each in the scenario.</div>
                        </div>
                        <div className="settings-content">
                            <div className="scenario-roles">
                                { selectedScenario.published ? <div className="scenario-disabled"></div> : <></> }
                                <div className="scenario-roles-labels">
                                    <div className="scenario-roles-label">Role</div>
                                    <div className="scenario-roles-label">Available</div>
                                </div>
                                
                                {totalRoles.sort((a, b) => b.order-a.order).reverse().map((rc: any, i: number) => {
                                    return (<div className="scenario-role" key={i}>
                                            <input className="scenario-role-text" value={rc.text}  data-role={rc.text} data-order={rc.order} onChange={changeRoleText} />
                                            <input className="scenario-role-num" value={rc.count} data-role={rc.text} data-order={rc.order} onChange={changeRoleNum} onFocus={e => e.target.select()} />
                                            { i === 0 ? <></> : <div className="delete-shared-user" data-role={rc.text} data-order={rc.order} onClick={deleteParticipant} data-i={i}>
                                                <img src="img/white-x.svg" alt="x" />
                                            </div> }
                                        </div>
                                    );
                                })}
                                <div className="scenario-role">
                                    <input className="scenario-role-text" value={newParticipant} onChange={changeNewParticipant} />
                                    <input className="scenario-role-num" value={newAvailRoles} onChange={changeAvailRoles} />
                                    <div className="add-shared-user" onClick={addNewParticipant}>
                                        + 
                                    </div>
                                </div>
                                <div className="scenario-role">
                                    <div className="total-roles-label">Total Roles:</div>
                                    <div className="total-roles-value">{totalRoleCount}</div>
                                    <div className={`role-count-high ${roleCountHigh}`}>{maxRoleCount} is the max number of roles allowed on this scenario.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="settings-row roles-row">
                        <div className="settings-label roles-label">
                            <div className="settings-label-title">Shared With</div>
                            <div className="settings-label-desc">List of IDs/Emails to share Scenario with.</div>
                        </div>
                        <div className="settings-content">
                            <div className="scenario-roles">
                            { selectedScenario.published ? <div className="scenario-disabled"></div> : <></> }
                                <div className="scenario-roles-labels">
                                    <div className="scenario-roles-label">UserID/Email</div>
                                </div>
                                
                                {selectedScenario.sharedWith.map((user, i) => {
                                    return (<div className="scenario-role" key={i}>
                                            <input className="scenario-role-text" value={user} onChange={changeSharedWith} data-i={i} />
                                            <div className="delete-shared-user" onClick={deleteSharedWith} data-i={i}>
                                                <img src="img/white-x.svg" alt="x" />
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="scenario-role">
                                    <input className="scenario-role-text" value={newUser} onChange={changeNewSharedWith} />
                                    <div className="add-shared-user" onClick={addSharedWith}>
                                        + 
                                    </div>
                                </div>
                                
                            </div>
                        </div>
                    </div>
                    <div className="settings-row votes-row">
                        <div className="settings-label votes-label">
                            <div className="settings-label-title">Votes</div>
                            <div className="settings-label-desc">Configure the style of voting used in the scenario</div>
                        </div>
                        <div className="settings-content">
                            <div className="voting-content">
                            { selectedScenario.published ? <div className="scenario-disabled"></div> : <></> }
                                <div className="voting-btns">
                                    <div className={`voting-btn ${majoritySelected}`} onClick={selectVotingStyle} data-id="majority">Majority</div>
                                    <div className={`voting-btn ${unanimousSelected}`} onClick={selectVotingStyle} data-id="unanimous">Unanimous</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={deleteGameModalIsOpen}
                onRequestClose={closeDeleteGameModal}
                style={defaultModalStyles}
                contentLabel="Delete Game Modal"
            >
                {deletableGame && <>
                <div className="new-modal-header" style={{width: 'calc(100% - 15px)'}}>Delete Game? <img src="/img/light-grey-x.svg" className="new-modal-padding-close" alt="x" onClick={closeDeleteGameModal}></img></div>
                <div className="new-modal-text">
                    <span className="modal-scenario-name">{deletableGame.name}</span> {deletableGame.started ? 'has been started' : 'has not been started'} and has {deletableGame.playerCount} player(s). <br/>
                    Are you sure you want to delete this game? <br/>
                    You cannot undo this action
                </div>

                <button onClick={closeDeleteGameModal} className="new-cancel-btn"><span>Cancel</span></button>
                <button onClick={onDeleteGame} className={`new-confirm-btn ${deleteGameLoading ? 'start-loading' : ''}`}>
                    {deleteGameLoading ? <div className="game-save-loader-25"></div> : <></> }
                    <span>Delete</span>
                </button>
                </>}
            </Modal>
        </div>
    );
}

export default ScenarioSettings;